import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Components
import PackageStatus from "../PackageStatus";
import SerialNumber from "../SerialNumber";
import PackageDetails from "../PackageDetails";
import PackageImages from "./PackageImages";
import PackageItems from "./PackageItems";
import EditConsolidatedPackageDrawer from "../EditConsolidatedPackageDrawer";
import ReceivingSlip from "./ReceivingSlip";
import AccordionViaTableRowName from "../AccordionViaTableRowName";

// Icons
import edit from "../../assets/svg/edit.svg";
import slip from "../../assets/svg/slip.svg";

// Firebase
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

// Helpers
import { sendNotification, getDetailsById, sendNotificationFunction, getUserDetailsById } from "../../helper/functions";
import { updatePackageColidationStatus, availableSerialNumber, updateSerialNumber } from "../helper";
import { calculateTotalValues } from "../../pages/Shipment/shippingRequest/helper";
import AddItemModal from "./AddItemModel";
import { setActiveItem, setSelectedRequest } from "../../store/commonSlice";

const ConsolidationTracking = ({ steps, activeStep, selectedPackages }) => {
  const dispatch = useDispatch();
  const { selectedRequest: userActiveData, selectedPackage: packageActiveData, selectedItem } = useSelector((state) => state.common);

  // Local state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editPackageData, setEditPackageData] = useState({});
  const [packageItems, setPackageItems] = useState([]);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [slipValue, setSlipValue] = useState("");
  const [dateForSlip, setDateForSlip] = useState();
  const [addItemModelOpen, setAddItemModelOpen] = useState(false);

  console.log(selectedItem, "selectedItem");

  // Generate random values for barcode and slip
  const generateRandomBarcodeValue = () => {
    return Math.floor(100000000 + Math.random() * 900000000).toString();
  };

  const generateSlipNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };

  // Initialize values
  useEffect(() => {
    setBarcodeValue(generateRandomBarcodeValue());
    setSlipValue(generateSlipNumber());
  }, []);

  // Set slip date when package arrives
  useEffect(() => {
    if (packageActiveData?.arrivedAt?.seconds) {
      const date = new Date(packageActiveData.arrivedAt.seconds * 1000);
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      }).replace(/\//g, ".");
      setDateForSlip(formattedDate);
    }
  }, [packageActiveData?.arrivedAt]);

  // Fetch package items
  useEffect(() => {
    const fetchPackageItems = async () => {
      if (!packageActiveData?.packageItemIds?.length) {
        setPackageItems([]);
        return;
      }

      try {
        const itemPromises = packageActiveData.packageItemIds.map(async (itemId) => {
          if (!itemId) return null;
          const itemDocRef = doc(db, "items", itemId);
          const itemDocSnap = await getDoc(itemDocRef);
          return itemDocSnap.exists() ? { ...itemDocSnap.data(), id: itemId } : null;
        });

        const items = await Promise.all(itemPromises);
        setPackageItems(items.filter(Boolean));
      } catch (error) {
        console.error("Error fetching package items:", error);
        setPackageItems([]);
      }
    };

    fetchPackageItems();
  }, [packageActiveData?.packageItemIds]);

  // Handle prepare consolidation
  const handlePrepareConsolidation = async (packageId) => {
    try {
      if (userActiveData?.status !== "CONSOLIDATION_REQUESTED") {
        const docRef = doc(db, "shelfConsolidatedPackages", userActiveData?.id);
        await updateDoc(docRef, {
          status: userActiveData?.status === "CONSOLIDATION_PREPARE_SHIPMENT_REQUESTED"
            ? "CONSOLIDATION_PREPARE_SHIPMENT_REQUEST_APPROVED"
            : "CONSOLIDATION_RE_PREPARE_PACKAGE_COMPLETED",
        });

        const updatedPackageDetails = await getDetailsById(
          [{ key: "__name__", operator: "==", value: packageActiveData?.id }],
          "shelfConsolidatedPackages"
        );

        await updatePackageColidationStatus(
          updatedPackageDetails[0]?.packageIds,
          {
            packageStatus: userActiveData?.status === "CONSOLIDATION_PREPARE_SHIPMENT_REQUEST_APPROVED"
              ? "PREPARE_SHIPMENT_REQUEST_APPROVED"
              : "RE_PREPARE_PACKAGE_COMPLETED",
            isConsolidatedPackage: true
          }
        );
        const userDetails = await getUserDetailsById(updatedPackageDetails[0]?.userId);
        await sendNotificationFunction({
          eventType: "BUNDLE",
          eventStatus: "CONSOLIDATION_PREPARE_SHIPMENT_REQUEST_APPROVED",
          fcmToken: userDetails?.fcmToken,
          userId: userDetails?.id,
          mailId: userDetails?.emailId,
          payload: {
            bundleId: updatedPackageDetails?.id,
          }
        })
        toast.success('Status Updated Successfully!');
      } else {
        let newData = {};
        let packageGlobal = {};

        if (!userActiveData?.serialNumber) {
          const docSnap = await getDoc(doc(db, "masterData", "packageSetting"));
          if (docSnap.exists()) {
            packageGlobal = docSnap.data();
            const serialNo = availableSerialNumber(packageGlobal);
            newData["serialNumber"] = serialNo;
          }
        }

        const docRef = doc(db, "shelfConsolidatedPackages", userActiveData?.id);
        const removePackageIds = userActiveData.removePackageIds || [];

        // Remove packages from consolidation
        for (const id of removePackageIds) {
          const packageRef = doc(db, "shelfPackages", id);
          await updateDoc(packageRef, { isPackageConsolidated: false });
        }

        const updatedPackageIds = userActiveData.packageIds.filter(
          (id) => !removePackageIds.includes(id)
        );

        const updatedConsolidation = await calculateTotalValues(updatedPackageIds);

        await updateDoc(docRef, {
          ...newData,
          status: "CONSOLIDATION_APPROVED",
          preparedShipmentDate: new Date(),
          packageIds: updatedPackageIds,
          removePackageIds: [],
          packageItemIds: updatedConsolidation?.packageItemIds,
          totalAmount: updatedConsolidation?.totalAmount,
          totalWeight: updatedConsolidation?.totalWeight,
          merchantCount: updatedConsolidation?.merchantCount,
        });
        dispatch(setSelectedRequest({
          ...userActiveData,
          status: "CONSOLIDATION_APPROVED",
          preparedShipmentDate: new Date(),
          packageIds: updatedPackageIds,
          removePackageIds: [],
          packageItemIds: updatedConsolidation?.packageItemIds,
          totalAmount: updatedConsolidation?.totalAmount,
          totalWeight: updatedConsolidation?.totalWeight,
          merchantCount: updatedConsolidation?.merchantCount,
        }));
        if (!userActiveData?.serialNumber) {
          await updateSerialNumber(packageGlobal);
          await updatePackageColidationStatus(updatedPackageIds, {
            packageStatus: "RECEIVED_AT_WAREHOUSE",
            isConsolidatedPackage: true
          });

          const updatedPackageDetails = await getDetailsById(
            [{ key: "__name__", operator: "==", value: packageActiveData?.id }],
            "shelfConsolidatedPackages"
          );

          // await sendNotification(
          //   updatedPackageDetails[0]?.userId,
          //   "Package is ready for shipment",
          //   `Your Bundle ${updatedPackageDetails[0]?.consolidatedPackageName} is now ready for shipment. Please proceed to selecting your shipping option for shipping and delivery.`,
          //   { number: "", type: "BUNDLE-UP" },
          //   "Select Shipment"
          // );

          const userDetails = await getUserDetailsById(updatedPackageDetails[0]?.userId);
          await sendNotificationFunction({
            eventType: "BUNDLE",
            eventStatus: "CONSOLIDATION_APPROVED",
            fcmToken: userDetails?.fcmToken,
            userId: userDetails?.id,
            mailId: userDetails?.emailId,
            payload: {
              bundleId: updatedPackageDetails[0]?.id,
              packageIds: updatedPackageDetails[0]?.packageIds,
              userName: userDetails?.name,
            }
          })
        }

        toast.success("Consolidated request prepared successfully");
      }
    } catch (error) {
      console.error("Error preparing consolidation:", error);
      toast.error("Failed to prepare consolidation");
    }
  };

  // Toggle edit drawer
  const toggleDrawer = (packageData) => {
    setEditPackageData(packageData);
    setDrawerOpen(!drawerOpen);
  };

  // Handle slip download
  const handleSlipDownload = async () => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const { default: jsPDF } = await import("jspdf");

      const slipElement = document.getElementById("receiving-slip");
      if (!slipElement) return;

      const canvas = await html2canvas(slipElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${packageActiveData?.suiteNumber || "package"}_slip.pdf`);
    } catch (error) {
      console.error("Error generating slip:", error);
      toast.error("Failed to generate slip");
    }
  };

  if (!packageActiveData) {
    return (
      <Box sx={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Select a package to view details
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {!selectedItem ? (
        <Box sx={{ paddingTop: "16px", paddingRight: "20px" }}>
          {/* Header */}
          <Box sx={{ marginLeft: "20px", mb: "16px" }}>
            <Typography variant="h5">
              Tracking: {packageActiveData?.trackingNumber || "N/A"}
            </Typography>
          </Box>

          {/* Package Status */}
          <PackageStatus steps={steps} activeStep={activeStep} />

          {/* Serial Number and Package Details */}
          <SerialNumber
            SerialNumber={packageActiveData?.serialNumber || "-"}
            packageTotalAmount={packageActiveData?.packageTotalAmount || packageActiveData?.totalAmount}
            shipmentData={userActiveData}
            pageName="consolidateRequest"
          />

          <PackageDetails packageActiveData={packageActiveData} />

          {/* Action Buttons */}
          <Box
            sx={{
              marginTop: "40px",
              display: "flex",
              justifyContent: "center",
              borderBottom: "1px solid #E5E5E8",
              paddingBottom: "30px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {/* Edit Package Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                borderRadius: "4px",
                boxShadow: "none",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
              onClick={() => toggleDrawer(packageActiveData)}
            >
              <img src={edit} alt="edit" style={{ marginRight: "10px" }} />
              Edit Package Details
            </Button>

            {/* Receiving Slip Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                boxShadow: "none",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
              disabled={!packageActiveData?.serialNumber}
              onClick={handleSlipDownload}
            >
              <img src={slip} alt="slip" style={{ marginRight: "10px" }} />
              Receiving Slip
            </Button>

            {/* Prepare Consolidation Button */}
            <Button
              variant="contained"
              onClick={() =>
                (packageActiveData?.dimensionW && packageActiveData?.dimensionL && packageActiveData?.dimensionH)
                  ? handlePrepareConsolidation(packageActiveData?.id)
                  : toggleDrawer(packageActiveData)
              }
              disabled={
                !(["CONSOLIDATION_REQUESTED", "CONSOLIDATION_PREPARE_SHIPMENT_REQUESTED", "CONSOLIDATION_RE_PREPARE_PACKAGE"].includes(userActiveData?.status)) ||
                (userActiveData?.status === "CONSOLIDATION_REQUESTED" && userActiveData?.packageIds?.length !== selectedPackages?.length)
              }
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                boxShadow: "none",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
            >
              {["CONSOLIDATION_REQUESTED"].includes(userActiveData?.status)
                ? `Prepare Consolidation`
                : ["CONSOLIDATION_APPROVED", "CONSOLIDATION_PREPARE_SHIPMENT_REQUESTED", "CONSOLIDATION_PREPARE_SHIPMENT_REQUEST_APPROVED"].includes(userActiveData?.status)
                  ? `Prepare Shipment`
                  : `Reprepare Consolidation`
              }
              {userActiveData?.status === "CONSOLIDATION_REQUESTED" && (
                <span className="text-lg ps-2">
                  {selectedPackages?.length || 0}
                </span>
              )}
            </Button>
          </Box>

          {/* Edit Package Drawer */}
          <EditConsolidatedPackageDrawer
            pageName="consolidateRequest"
            editPackage={editPackageData}
            open={drawerOpen}
            onClose={toggleDrawer}
            collectionName="shelfConsolidatedPackages"
            shipmentCollectionName="consolidatedShipmentRequests"
            shipmentData={userActiveData}
          />

          {/* Hidden Receiving Slip for PDF generation */}
          <Box sx={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
            <ReceivingSlip
              id="receiving-slip"
              name={packageActiveData?.suiteNumber ? packageActiveData?.name : "No Name"}
              date={dateForSlip}
              suiteNumber={packageActiveData?.suiteNumber ? packageActiveData?.suiteNumber : "No Suite"}
              packageId={packageActiveData?.packageId}
              barcodeValue={packageActiveData?.serialNumber ? packageActiveData?.serialNumber : barcodeValue}
            />
          </Box>

          {/* Package Images */}
          <PackageImages
            images={packageActiveData?.packageImages}
            title="Package Images"
          />
          {console.log(packageActiveData, "packageActiveDataddd")}
          {/* Package Items */}
          <PackageItems
            items={packageItems}
            packageActiveData={packageActiveData}
            userActiveData={userActiveData}
            collection="shelfConsolidatedPackages"
            shipmentCollection="consolidatedShipmentRequests"
            addItemButton={
              !packageActiveData?.isConsolidationPackage ? <Button
                color="primary"
                onClick={() => {
                  setAddItemModelOpen(true);
                }}
              >
                Add Item
              </Button>
                : null
            }
          />
          <AddItemModal
            open={addItemModelOpen}
            toggleModel={() => setAddItemModelOpen(!addItemModelOpen)}
          />
        </Box>
      ) : selectedItem ? (
        <AccordionViaTableRowName
          handleBack={() => { dispatch(setActiveItem(null)) }}
          packageClickHandler={() => { dispatch(setActiveItem(null)) }}
          id={selectedItem}
          packageActiveData={packageActiveData}
          userActiveData={userActiveData}
          packageItems={packageItems}
          pageName="consolidateRequest"
          packageCollection="shelfConsolidatedPackages"
          shipmentCollection="consolidatedShipmentRequests"
        />
      ) : null}
    </>
  );
};

export default ConsolidationTracking; 